import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaFileAlt } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import { useUrgencyTabs, UrgencyTabBar, mapUrgencyToTab, CATEGORIES } from '../components/UrgencyTabs';
import ToggleSwitch from '../components/ToggleSwitch';
import { hasAnyAnswers, buildQuestionsPlainText, buildQuestionsDocxBlocks, downloadFile, getUniqueAnswerDates, ALL_DATES_OPTION } from '../../utils/questionsExport';
import { createStructuredDocxFile } from '../../utils/docxBuilder';
import { getAnswerTranslation } from '../../utils/answerTranslation';
import '../mobile.css';

const MobileQuestions = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setFullFIR } = useCaseContext();
  const { id, from, name, interrogationQuestions = [], statement: isStatement } = location.state || {};

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [statusByIndex, setStatusByIndex] = useState(() => {
    const initial = {};
    interrogationQuestions.forEach((q, i) => { initial[i] = !!q.status; });
    return initial;
  });

  const { activeTab, setActiveTab, hasUrgency, tabCounts, visibleItems } = useUrgencyTabs(
    interrogationQuestions.map((q, i) => ({ ...q, __index: i })),
    (q) => q.category
  );

  const pendingCounts = {};
  CATEGORIES.forEach((cat) => {
    pendingCounts[cat] = interrogationQuestions
      .map((q, i) => ({ ...q, __index: i }))
      .filter((q) => mapUrgencyToTab(q.category) === cat)
      .filter((q) => statusByIndex[q.__index] !== true).length;
  });

  const [showExportPopup, setShowExportPopup] = useState(false);
  const [exportSelected, setExportSelected] = useState({ Top: true, High: true, Medium: true, Low: true });
  const [exportIncludeAnswers, setExportIncludeAnswers] = useState(true);
  const [exportDateWise, setExportDateWise] = useState(false);
  const [exportSelectedDate, setExportSelectedDate] = useState(ALL_DATES_OPTION);
  const [isDownloading, setIsDownloading] = useState(false);
  const [exportError, setExportError] = useState('');

  const openExportPopup = () => {
    setExportSelected({ Top: true, High: true, Medium: true, Low: true });
    setExportIncludeAnswers(true);
    setExportDateWise(false);
    setExportSelectedDate(ALL_DATES_OPTION);
    setExportError('');
    setShowExportPopup(true);
  };

  const getExportSelectedQuestions = () =>
    interrogationQuestions.filter((q) => exportSelected[mapUrgencyToTab(q.category)]);

  // Used both as the bold Word-document heading and as the WhatsApp message's
  // intro line.
  const exportHeading = `${from === 'Witness' ? 'Examination' : 'Interrogation'} of ${name || ''}`;

  const handleShare = () => {
    const picked = getExportSelectedQuestions();
    const text = buildQuestionsPlainText(picked, exportIncludeAnswers);

    if (!text.trim()) {
      setExportError('No questions match the selected categories.');
      return;
    }

    const waLink = `https://wa.me/?text=${encodeURIComponent(`${exportHeading}\n\n${text}`)}`;
    window.open(waLink, '_blank');
    setShowExportPopup(false);
  };

  const handleDownload = async () => {
    const picked = getExportSelectedQuestions();

    if (picked.length === 0) {
      setExportError('No questions match the selected categories.');
      return;
    }

    const dateWiseActive = exportIncludeAnswers && exportDateWise;

    if (dateWiseActive) {
      const uniqueDates = getUniqueAnswerDates(picked);
      if (exportSelectedDate === ALL_DATES_OPTION && uniqueDates.length === 0) {
        setExportError('No dated answers found for the selected categories.');
        return;
      }
      if (exportSelectedDate !== ALL_DATES_OPTION && !uniqueDates.some((d) => d.label === exportSelectedDate)) {
        setExportError('No answered questions match the selected date.');
        return;
      }
    }

    setIsDownloading(true);
    setExportError('');

    try {
      const blocks = buildQuestionsDocxBlocks({
        heading: exportHeading,
        questions: picked,
        includeAnswers: exportIncludeAnswers,
        dateWise: dateWiseActive,
        selectedDate: exportSelectedDate,
      });
      const file = await createStructuredDocxFile(blocks, `${name || 'questions'}_${from === 'Witness' ? 'Examination' : 'Interrogation'}_Questions.docx`);
      downloadFile(file);
      setShowExportPopup(false);
    } catch {
      setExportError('Failed to generate the Word document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const toggleStatus = async (index, question) => {
    const prev = !!statusByIndex[index];
    setStatusByIndex((s) => ({ ...s, [index]: !prev }));

    try {
      const api = createApiClient(API_URL);
      const response = await api.post(`/fir/iq/status/${id}`, {
        user: from,
        name,
        question: question.question,
        status: !prev ? 'completed' : 'pending',
      });
      if (response.status === 200) setFullFIR(response.data);
    } catch {
      setStatusByIndex((s) => ({ ...s, [index]: prev }));
    }
  };

  if (!interrogationQuestions.length) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No questions available.</p>;

  return (
    <div>
      {name && <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#094268', marginBottom: '10px' }}>{name}</h2>}

      <UrgencyTabBar hasUrgency={hasUrgency} activeTab={activeTab} setActiveTab={setActiveTab} tabCounts={tabCounts} badgeCounts={pendingCounts} />

      {visibleItems.map((item) => {
        const index = item.__index;
        const isOpen = expandedIndex === index;
        const isCompleted = !!statusByIndex[index];

        return (
          <div key={index} className="m-card" onClick={() => setExpandedIndex(isOpen ? null : index)} style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', textAlign: 'justify' }}>{item.question}</div>

            {isOpen && (
              <>
                {item.relevance && <div style={{ marginTop: '6px', textAlign: 'justify' }}><b>Relevance: </b>{item.relevance}</div>}
                {Array.isArray(item.answer) && item.answer.length > 0 && (
                  <div style={{ marginTop: '6px', textAlign: 'justify' }}>
                    <b>Answer</b>
                    {item.answer.map((ans, idx) => (
                      <div key={idx} style={{ marginTop: '4px' }}>
                        {idx + 1}. {ans}
                        {getAnswerTranslation(item, idx) && (
                          <div style={{ marginTop: '2px' }}><b>Translation: </b>{getAnswerTranslation(item, idx)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              {!isStatement ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  {!(Array.isArray(item.answer) && item.answer.length > 0) && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/m/fir/witnesses/questions/answer', {
                          state: { id, from, name, question: item.question, questionIndex: index, interrogationQuestions, statement: isStatement },
                        });
                      }}
                      className="m-pill-btn"
                      style={{ color: '#094268', fontWeight: 600, padding: '4px 10px', fontSize: '13px' }}
                    >
                      Record Answer
                    </button>
                  )}
                  <ToggleSwitch checked={isCompleted} onChange={() => toggleStatus(index, item)} />
                  <span style={{ fontSize: '13px', color: isCompleted ? '#4CAF50' : '#E53935' }}>
                    {isCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>
              ) : <span />}
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#5B7CFF' }}>{isOpen ? 'Hide' : 'Show'}</span>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={openExportPopup}
        className="m-chat-fab"
        style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
        aria-label="Share or download questions"
        title="Share or download questions"
      >
        <FaFileAlt size={22} color="#fff" />
      </button>

      {showExportPopup && (
        <div className="m-popup-overlay">
          <div className="m-popup-container">
            <h3>Share / Download Questions</h3>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '10px', marginBottom: '14px' }}>
              {CATEGORIES.map((label) => (
                <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={!!exportSelected[label]}
                    onChange={(e) => setExportSelected((prev) => ({ ...prev, [label]: e.target.checked }))}
                    style={{ width: '14px', height: '14px', margin: 0 }}
                  />
                  {label}
                </label>
              ))}
            </div>

            {hasAnyAnswers(interrogationQuestions) && (() => {
              const uniqueDates = getUniqueAnswerDates(getExportSelectedQuestions());
              return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={exportIncludeAnswers}
                      onChange={(e) => setExportIncludeAnswers(e.target.checked)}
                      style={{ width: '14px', height: '14px', margin: 0 }}
                    />
                    Include Answers
                  </label>

                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: exportIncludeAnswers ? 'pointer' : 'not-allowed',
                      whiteSpace: 'nowrap',
                      opacity: exportIncludeAnswers ? 1 : 0.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={exportDateWise}
                      disabled={!exportIncludeAnswers}
                      onChange={(e) => setExportDateWise(e.target.checked)}
                      style={{ width: '14px', height: '14px', margin: 0 }}
                    />
                    Date Wise
                  </label>

                  <select
                    value={exportSelectedDate}
                    disabled={!exportIncludeAnswers || !exportDateWise}
                    onChange={(e) => setExportSelectedDate(e.target.value)}
                    style={{ fontSize: '14px', padding: '4px 6px', position: 'relative', top: '-4px', opacity: exportIncludeAnswers && exportDateWise ? 1 : 0.5 }}
                  >
                    <option value={ALL_DATES_OPTION}>{ALL_DATES_OPTION}</option>
                    {uniqueDates.map((d) => (
                      <option key={d.raw} value={d.label}>{d.label}</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {exportError && <p style={{ color: '#c9302c', fontSize: '14px' }}>{exportError}</p>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowExportPopup(false)} className="m-pill-btn" style={{ color: '#094268', fontWeight: 600 }} disabled={isDownloading}>Cancel</button>
              <button onClick={handleShare} className="m-pill-btn" style={{ color: '#25D366', fontWeight: 600 }} disabled={isDownloading}>Share</button>
              <button className="m-button-primary" style={{ width: 'auto', padding: '8px 14px' }} onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <div className="m-spinner" style={{ margin: '0 auto' }} /> : 'Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileQuestions;
